import { useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Spinner,
  useDisclosure,
  type PartnerMembershipOut,
} from "@dearbill/shared";
import { usePartnerSession } from "../auth/usePartnerSession";
import { usePartnerMe } from "../auth/usePartnerMe";
import { partnerRoleLabel } from "../auth/roleLabel";
import { useApi } from "../auth/useApi";
import { NewOrgModal } from "../components/NewOrgModal";

/** Organizations and the next available step for each existing membership. */
export function OrgList(): ReactNode {
  const { session } = usePartnerSession();
  const { me, setMe, settled, error } = usePartnerMe();
  const navigate = useNavigate();
  // Only the open-state boolean is used. The modal owns its dialog
  // accessibility, so `triggerProps` and `panelProps` are not spread; they wire
  // aria-expanded and aria-controls for inline collapsibles, not a
  // role="dialog" modal.
  const newOrg = useDisclosure();
  // The dialog stays mounted through its close animation, so it is keyed per
  // opening to start each one with blank fields.
  const [newOrgKey, setNewOrgKey] = useState(0);

  if (!session) return <Navigate to="/login" replace />;

  // Append the new membership to the shared /me record, then navigate to the
  // org, where `OrgIndexRedirect` picks the tab. Navigating away unmounts
  // OrgList and the modal with it, so no explicit close is needed. The provider
  // loads `me` once, so the append keeps the org in the list when the user
  // returns through the Switch organization link. Only the success path runs
  // this; Cancel calls `newOrg.toggle` (`onClose`) alone.
  function handleCreated(membership: PartnerMembershipOut): void {
    if (me !== null) setMe({ ...me, memberships: [...me.memberships, membership] });
    void navigate(`/orgs/${membership.orgId}`);
  }

  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-10">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-h2 text-midnight font-serif">Your organizations</h1>
        {me?.accountRole === "owner" && (
          <Button
            onClick={() => {
              setNewOrgKey((k) => k + 1);
              newOrg.toggle();
            }}
          >
            New Organization
          </Button>
        )}
      </div>

      {error && (
        <Alert
          tone="error"
          title="We couldn't load your account. Please try again."
          className="mt-6"
        >
          Reload the page to try again — if it keeps happening, use “Need help? Call” at the top of
          the page.
        </Alert>
      )}

      {!settled && !error && (
        <div className="text-slate-teal mt-8 flex items-center gap-3">
          <Spinner /> <span>Loading your organizations…</span>
        </div>
      )}

      {me !== null && me.memberships.length === 0 && (
        <div className="border-border-strong mt-8 rounded-2xl border bg-white px-6 py-10 text-center">
          <p className="text-midnight font-semibold">
            You&apos;re not a member of any organizations yet.
          </p>
          <p className="text-slate-teal mt-1">
            {me.accountRole === "owner"
              ? "Organizations you create will be listed here. To get started, use the “New Organization” button above."
              : "Organizations you join will be listed here. Ask your organization's admin to invite you using this email address."}
          </p>
        </div>
      )}

      {me !== null && me.memberships.length > 0 && (
        <ul className="mt-6 space-y-3">
          {me.memberships.map((membership) => (
            <MembershipCard
              key={membership.orgId}
              membership={membership}
              onAccepted={(updated) => {
                setMe({
                  ...me,
                  memberships: me.memberships.map((item) =>
                    item.orgId === updated.orgId ? updated : item,
                  ),
                });
                void navigate(destination(updated));
              }}
            />
          ))}
        </ul>
      )}

      <NewOrgModal
        key={newOrgKey}
        open={newOrg.open && me !== null}
        onClose={newOrg.toggle}
        onCreated={handleCreated}
      />
    </main>
  );
}

/** Name + role block shared by the clickable and disabled membership cards. */
function MembershipCardBody({ membership }: { membership: PartnerMembershipOut }): ReactNode {
  return (
    <div className="min-w-0">
      <p className="text-midnight truncate font-semibold">{membership.orgName}</p>
      <p className="text-slate-teal mt-0.5 text-sm">{partnerRoleLabel(membership.role)}</p>
    </div>
  );
}

function destination(membership: PartnerMembershipOut): string {
  const needsSetup = ["profile_needed", "agreement_needed"].includes(membership.setupState);
  return `/orgs/${membership.orgId}${needsSetup ? "/organization" : ""}`;
}

function MembershipCard({
  membership,
  onAccepted,
}: {
  membership: PartnerMembershipOut;
  onAccepted: (membership: PartnerMembershipOut) => void;
}): ReactNode {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const state = membership.setupState;
  const admin = membership.role === "org_admin";
  const labels = {
    profile_needed: "Setup needed",
    agreement_needed: "Terms needed",
    review_in_progress: "Review in progress",
    active: "Active",
    invitation_pending: "Invitation",
    unavailable: membership.orgDisabled ? "Disabled" : "Access unavailable",
  };
  const messages = {
    profile_needed: admin
      ? "Continue with your saved organization details."
      : "Your organization administrator needs to complete the profile.",
    agreement_needed: admin
      ? "Review and accept DearBill’s Terms & Conditions."
      : "Your organization administrator needs to accept DearBill’s Terms & Conditions.",
    review_in_progress:
      "Setup is complete. DearBill is reviewing your organization. You can sign in and explore while you wait.",
    active: "",
    invitation_pending: "You’re invited to join this organization using your existing account.",
    unavailable: "Contact your organization administrator for help with access.",
  };
  const action =
    state === "profile_needed"
      ? admin
        ? "Continue setup"
        : "View setup"
      : state === "agreement_needed"
        ? admin
          ? "Accept terms"
          : "View setup"
        : "Open organization";
  async function accept(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const updated = await api<PartnerMembershipOut>(
        `/api/partner/orgs/${membership.orgId}/accept-invitation`,
        { method: "POST" },
      );
      onAccepted(updated);
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }
  const contents = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MembershipCardBody membership={membership} />
        <Badge tone={state === "active" ? "active" : state === "unavailable" ? "error" : "pending"}>
          {labels[state]}
        </Badge>
      </div>
      {messages[state] && <p className="text-slate-teal text-body-sm mt-3">{messages[state]}</p>}
    </>
  );
  const classes = "border-border-strong block rounded-2xl border bg-white px-5 py-4";
  return (
    <li>
      {state === "unavailable" ? (
        <div aria-disabled="true" className={classes}>
          {contents}
        </div>
      ) : state === "invitation_pending" ? (
        <div className={classes}>
          {contents}
          {failed && (
            <Alert
              tone="error"
              title="We couldn’t accept this invitation. Try again or contact your organization administrator."
              className="mt-3"
            />
          )}
          <Button onClick={() => void accept()} disabled={busy} className="mt-4">
            {busy ? "Accepting…" : "Accept invitation"}
          </Button>
        </div>
      ) : (
        <Link
          to={destination(membership)}
          className={`${classes} hover:border-cloud-blue hover:bg-ice transition-colors`}
        >
          {contents}
          <span className="text-primary-text mt-3 inline-block font-medium underline">
            {action}
          </span>
        </Link>
      )}
    </li>
  );
}
